import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from 'src/store/userSlice';
import {
  AdminRole,
  useLazyFetchUserProfileQuery,
  UserEntity,
} from 'src/store/userApi';
import { selectOptions } from 'src/store/optionSlice';
import { Spinner } from 'src/ui/Spinner';
import MatchesList from './matchesList';
import MatchedUserProfile from './matchedUserProfile';
import MatchDetails from './matchDetails';
import { UserMatch, useGetUserMatchesQuery } from 'src/store/matchTestApi';

interface CurrentUserBriefProps {
  user: UserEntity | null | undefined;
}

const CurrentUserBrief = ({ user }: CurrentUserBriefProps) => {
  const allOptions = useSelector(selectOptions);
  const [optionsMap, setOptionsMap] = useState<Record<string, any>>({});

  useEffect(() => {
    const optionKeys = Object.keys(allOptions);
    if (optionKeys.length) {
      const _optionsMap: { [key: string]: any } = {};
      optionKeys.forEach((key: any) => {
        const field = allOptions[key]?.field;
        _optionsMap[field] = allOptions[key];
      });
      setOptionsMap(_optionsMap);
    }
  }, [allOptions]);

  const decodeFieldValue = (fieldKey: string, value: any) => {
    if (!value && value !== 0) return '';

    if (optionsMap[fieldKey]) {
      const options = optionsMap[fieldKey].labels;
      const isBinary = options[1]?.[0] <= 2;

      if (isBinary) {
        return options
          .reduce((acc: any, cur: any) => {
            if (Number(value) & Math.pow(2, cur[0])) {
              acc.push(cur[1].cn || cur[1].en);
            }
            return acc;
          }, [])
          .join(', ');
      } else {
        const option = options.find((option: any) => option[0] === value);
        return option?.[1]?.cn || option?.[1]?.en || value;
      }
    }

    return value;
  };

  if (!user) {
    return (
      <div
        style={{
          padding: 12,
          borderBottom: '1px solid var(--border)',
          background: 'var(--blue-bg, #eff6ff)',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 'var(--fs-sm)',
            color: 'var(--blue-text, #1d4ed8)',
            marginBottom: 4,
          }}
        >
          Current User Profile
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 0',
          }}
        >
          <Spinner size="sm" />
        </div>
      </div>
    );
  }

  const name = user.profile?.name || user.email;
  const infoParts: string[] = [];

  if (user.profile?.gender) {
    infoParts.push(decodeFieldValue('gender', user.profile.gender));
  }
  if (user.profile?.age) {
    infoParts.push(String(user.profile.age));
  }

  const locationParts: string[] = [];
  if (user.profile?.city)
    locationParts.push(decodeFieldValue('city', user.profile.city));
  if (user.profile?.state)
    locationParts.push(decodeFieldValue('state', user.profile.state));
  if (user.profile?.country)
    locationParts.push(decodeFieldValue('country', user.profile.country));
  if (locationParts.length > 0) infoParts.push(locationParts.join(', '));

  const firstPhoto = user.photos?.[0];
  const photoUrl =
    firstPhoto?.sImage?.url ||
    firstPhoto?.mImage?.url ||
    firstPhoto?.lImage?.url;

  return (
    <div
      style={{
        padding: 12,
        borderBottom: '1px solid var(--border)',
        background: 'var(--blue-bg, #eff6ff)',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          fontWeight: 600,
          fontSize: 'var(--fs-sm)',
          color: 'var(--blue-text, #1d4ed8)',
          marginBottom: 4,
        }}
      >
        Current User Profile
      </div>
      <div className="row" style={{ gap: 12, alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontWeight: 500,
              fontSize: 'var(--fs-sm)',
              color: 'var(--text)',
              marginBottom: 2,
            }}
          >
            {name}
          </div>
          {infoParts.length > 0 && (
            <div
              style={{
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-secondary)',
              }}
            >
              {infoParts.join(' • ')}
            </div>
          )}
        </div>
        {photoUrl && (
          <div style={{ flexShrink: 0 }}>
            <img
              src={photoUrl}
              alt="Profile"
              style={{
                width: 50,
                height: 50,
                objectFit: 'cover',
                borderRadius: 'var(--radius-sm)',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const UserMatchesPage = () => {
  const [selectedMatch, setSelectedMatch] = useState<UserMatch | null>(null);
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentUser = useSelector(selectUser);
  const [fetchCurrentUserProfile, { data: currentUserProfile }] =
    useLazyFetchUserProfileQuery();
  const { data: matchesData } = useGetUserMatchesQuery(userId || '', {
    skip: !userId,
  });

  const isAdmin =
    currentUser?.adminRole && currentUser.adminRole >= AdminRole.ADMIN;

  useEffect(() => {
    if (!isAdmin) {
      navigate('/interviews');
      return;
    }

    if (!userId) {
      navigate('/user-profile');
      return;
    }

    // Fetch the current user's profile
    if (userId) {
      fetchCurrentUserProfile(userId);
    }
  }, [userId, isAdmin, navigate, fetchCurrentUserProfile]);

  // Auto-select match from URL query parameter
  useEffect(() => {
    const matchIdParam = searchParams.get('matchId');
    if (matchIdParam && matchesData?.matches) {
      const matchId = parseInt(matchIdParam, 10);
      const match = matchesData.matches.find((m) => m.matchId === matchId);
      if (match && (!selectedMatch || selectedMatch.matchId !== matchId)) {
        setSelectedMatch(match);
      }
    }
  }, [searchParams, matchesData, selectedMatch]);

  const handleMatchSelect = (match: UserMatch | null) => {
    setSelectedMatch(match);
  };

  if (!isAdmin || !userId) {
    return null;
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '400px 1fr 400px',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden',
      }}
    >
      {/* Left Column - Matches List */}
      <div
        style={{
          borderRight: '1px solid var(--border)',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <MatchesList
          userId={userId}
          selectedMatchId={selectedMatch?.matchId}
          onMatchSelect={handleMatchSelect}
        />
      </div>

      {/* Middle Column - Current User Brief + Matched User Profile */}
      <div
        style={{
          borderRight: '1px solid var(--border)',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CurrentUserBrief user={currentUserProfile} />
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <MatchedUserProfile
            userId={selectedMatch?.matchedUser.id}
            matchedUser={selectedMatch?.matchedUser}
          />
        </div>
      </div>

      {/* Right Column - Match Details */}
      <div style={{ height: '100%', overflow: 'hidden' }}>
        <MatchDetails matchId={selectedMatch?.matchId} match={selectedMatch} />
      </div>
    </div>
  );
};

export default UserMatchesPage;
